"use client";

import { motion, useTransform } from "motion/react";
import type { ComponentProps } from "react";
import { chartCssVars } from "../chart-context";
import { useChartProjection } from "./chart-projection-context";
import {
  type Box3,
  type BoxPaths,
  boxPaths,
  FACE_FRONT,
  FACE_SIDE,
  lerp,
} from "./iso-projection";
import { useProjectedValue, useProjectionProgress } from "./use-projected-path";

export const FRONT_SHADE = 84;
export const SIDE_SHADE = 68;

export const shadeFill = (fill: string, percent: number): string =>
  percent >= 100
    ? fill
    : `color-mix(in oklab, ${fill} ${Math.round(percent * 10) / 10}%, black)`;

const EMPTY_PATHS: BoxPaths = { top: "", front: "", side: "", faces: 0 };

export interface ProjectedBoxProps
  extends Omit<ComponentProps<typeof motion.g>, "children" | "fill"> {
  box: Box3 | ((e: number) => Box3);
  fill: string;
  hovered?: boolean;
  hoverStroke?: string;
}

export function ProjectedBox({
  box,
  fill,
  hovered = false,
  hoverStroke = chartCssVars.foreground,
  ...groupProps
}: ProjectedBoxProps) {
  const projection = useChartProjection();
  const e = useProjectionProgress(projection);
  const paths = useProjectedValue(
    projection,
    (view) => boxPaths(view, typeof box === "function" ? box(view.e) : box),
    EMPTY_PATHS
  );
  const top = useTransform(paths, (p) => p.top);
  const front = useTransform(paths, (p) =>
    p.faces & FACE_FRONT ? p.front : ""
  );
  const side = useTransform(paths, (p) => (p.faces & FACE_SIDE ? p.side : ""));
  const frontFill = useTransform(e, (v) =>
    shadeFill(fill, lerp(100, FRONT_SHADE, v))
  );
  const sideFill = useTransform(e, (v) =>
    shadeFill(fill, lerp(100, SIDE_SHADE, v))
  );

  if (!projection) {
    return null;
  }

  const outline = hovered && projection.isometric;

  return (
    <motion.g {...groupProps}>
      <motion.path d={side} style={{ fill: sideFill }} />
      <motion.path d={front} style={{ fill: frontFill }} />
      <motion.path
        d={top}
        fill={fill}
        stroke={outline ? hoverStroke : "none"}
        strokeWidth={outline ? 1 : 0}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </motion.g>
  );
}
